import Image from "next/image";
import React from "react";
import cx from "classnames";
import helper from "utils/helper";

interface ImageProps {
  wrapClassName?: string;
  className?: string;
  src: string;
  alt?: string;
  objectFit?: string;
  layout?: string;
  aspectRatio?: number;
  isRelative?: boolean;
  defaultImage?: React.ReactNode;
  onLoad?: (e: any) => void;
  [key: string]: any;
}

export default function CustomImage({
  wrapClassName,
  className,
  src,
  alt = "merchant-web-app",
  objectFit = "cover",
  layout = "fill",
  aspectRatio,
  isRelative = false,
  defaultImage,
  onLoad,
  ...props
}: ImageProps) {
  // Ensure alt is never undefined
  const imageAlt = alt || "merchant-web-app";

  // Check if URL is already absolute (starts with http:// or https://)
  const isAbsoluteUrl = (url: any): boolean => {
    if (typeof url !== 'string' || !url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const processImageUrl = (urlInput: any): string => {
    // Handle non-string inputs
    if (!urlInput) return "";
    const url = typeof urlInput === 'string' ? urlInput : String(urlInput);

    // Return empty for invalid URLs
    if (!url || url.trim() === "") return "";

    if (isRelative) return url;
    if (isAbsoluteUrl(url)) return url; // Don't process absolute URLs

    // Process through helper and validate result
    const processedUrl = helper.imageUrl(url);
    // If helper returns empty or invalid, return empty
    if (!processedUrl || processedUrl.trim() === "") return "";

    return processedUrl;
  };

  const initialSrc = React.useMemo(() => {
    return processImageUrl(src);
  }, [src, isRelative]);

  const [imageSrc, $imageSrc] = React.useState(initialSrc);
  const [imageStatus, setImageStatus] = React.useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [failedSrc, setFailedSrc] = React.useState<string>('');

  React.useEffect(() => {
    const newSrc = processImageUrl(src);
    // Only update if it's a different source and not a previously failed one
    if (newSrc !== imageSrc && newSrc !== failedSrc) {
      $imageSrc(newSrc);
      setImageStatus(newSrc ? 'loading' : 'idle');
    }
  }, [src, isRelative, imageSrc, failedSrc]);

  // Validate imageSrc is a valid URL or relative path
  const isValidImageSrc = (src: string): boolean => {
    if (!src || src.trim() === "") return false;
    // Check if it's a valid relative path or absolute URL
    if (src.startsWith('/')) return true;
    if (src.startsWith('http://') || src.startsWith('https://')) return true;
    // Check if it's a valid relative path without leading slash
    if (src.match(/^[a-zA-Z0-9][\w\-\.\/]*$/)) return true;
    return false;
  };

  // Check if URL is a proxy URL that will redirect
  const isProxyUrl = imageSrc?.includes('localhost:3001/api/media');

  // For proxy URLs, we still need to wrap in a div for size control
  if (isProxyUrl && imageSrc && isValidImageSrc(imageSrc)) {
    return (
      <div className={cx("relative", wrapClassName)}>
        <img
          {...props}
          className={className || "w-full h-full"}
          style={{
            aspectRatio,
            objectFit: objectFit as any,
            width: '100%',
            height: '100%',
            position: layout === "fill" ? 'absolute' : 'relative',
            inset: layout === "fill" ? 0 : undefined,
          }}
          src={imageSrc}
          alt={imageAlt}
          onLoad={(e) => {
            setImageStatus('loaded');
            onLoad?.(e);
          }}
          onError={() => {
            console.error('Image failed to load:', imageSrc);
            setImageStatus('error');
            setFailedSrc(imageSrc);
            $imageSrc("");
          }}
        />
      </div>
    );
  }

  return (
    <div className={cx("relative", wrapClassName)}>
      {!!imageSrc && isValidImageSrc(imageSrc) && (
        <Image
          {...props}
          className={className || "w-full h-full"}
          style={{
            aspectRatio,
            objectFit: objectFit as any,
          }}
          src={imageSrc}
          alt={imageAlt}
          priority
          fill={layout === "fill"}
          onLoad={(e) => {
            setImageStatus('loaded');
            onLoad?.(e);
          }}
          onError={() => {
            setImageStatus('error');
            setFailedSrc(imageSrc);
            $imageSrc("");
          }}
        />
      )}
      {(!imageSrc || !isValidImageSrc(imageSrc) || imageStatus === 'error') && (
        <div
          className="w-full h-full rounded-md bg-gray-100 flex items-center justify-center"
          style={{ aspectRatio }}
          {...props}
        >
          {defaultImage || (
            <div className="text-gray-400 text-center p-2">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mx-auto mb-1"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span className="text-xs">Image not available</span>
            </div>
          )}
        </div>
      )}
      {imageStatus === 'loading' && imageSrc && isValidImageSrc(imageSrc) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-md">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
